import { createContext, useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSymphonyApiService } from "../../../hooks/useSymphonyApi";
import { useChatHistory } from "./ChatHistoryContext";
import { ToastContext } from "cai-fusion";
import env from "../../../configs/envConfig";
import { debugLog } from "../../../configs/loggingConfig";

const ChatContext = createContext();

function ChatProvider({ children }) {
	const apiServiceClient = useSymphonyApiService("maestro", "v3");

	const { chats, chatsRef, dynamicPin, dynamicRename, dynamicNewChat, dynamicDelete } = useChatHistory();

	const navigate = useNavigate();

	const { createErrorToast } = useContext(ToastContext);

	// CHATID RULES
	// ------------
	// Use the chatId state for useEffect triggers only.
	// When in a function that needs a chatId, call fetchChatId.
	// When conditionally doing things based on whether we have an active chatId, use isChatInContext.

	// Current chat ID. Defaults to the one provided by the URL, if loaded from that.
	// This initial URL setting happens in symphonyApp, as it needs to be in-route to effectively grab the ID.
	// setChatId SHOULD NOT BE USED OUTSIDE OF THIS CASE.
	const [chatId, setChatId] = useState(undefined);
	const chatIdRef = useRef(chatId);
	// Object of the current chat.
	const [chat, setChat] = useState(undefined);

	// Pin state of the given chat
	const [isPinned, setIsPinned] = useState(false);

	const [loadingChat, setLoadingChat] = useState(false);

	const [isExporting, setIsExporting] = useState(false);

	// Const for the amount of tokens reserved by the system.
	const systemReservedTokens = parseInt(env.REACT_APP_SYSTEM_RESERVED_TOKENS ?? 800);

	useEffect(() => {
		debugLog.log("[CHAT] chatId changed to:", chatId);
		chatIdRef.current = chatId;
		handleChatChange();
	}, [chatId]);

	// This useEffect exists to update the name of the chat object if the chat history changes.
	useEffect(() => {
		let currChat = chats.find((chatItem) => chatItem?.chatId === chat?.chatId);
		if (currChat) {
			setChat({...chat, "name": currChat?.name})
		}
	}, [chats])

	// This method will set the chat ID if there currently is none. It is called by symphonyApp, which is loaded when a chat is showing.
	// Any other chat ID changes happen from other operations this context controls.
	const processUrlParameters = (urlChatId) => {
		if (chatId !== urlChatId) {
			debugLog.log("[CHAT] Noticed that the URL's chat ID is not equal to the current, updating the state!");
			setChatId(urlChatId);
		}
	}

	const handleChatChange = async () => {
		// If there is an available chatId, load up all necessary resources associated with it.
		if (chatId) {
			// Try and grab the applicable chat.
			try {
				// Get EVERYTHING
				setLoadingChat(true);
				debugLog.log("[CHAT] Attempting to find selected chat with ID", chatId);
				const respChat = await apiServiceClient.Chats.getChat(chatId, [
					"messages",
					"model",
					"modelparameters",
					"assistantinstruction",
					"chatuseraccesses",
					"datastores",
				]);
				debugLog.log("[CHAT] Success! Here's the chat object.", respChat);
				setChat(respChat);

				setIsPinned(respChat.isPinned);

				// Make sure we reference an up-to-date list of the chats to check if it's in the list.
				const isChatIn = chatsRef.current.find((chat) => chat.chatId === respChat.chatId);
				if (!isChatIn) dynamicNewChat(respChat);

				document.title = `${env.REACT_APP_BRANDING_SYMPHONY_NAME ?? "Maestro"} - ` + (respChat.name ?? "New Chat");
				setLoadingChat(false);
			} catch (error) {
				debugLog.log("[CHAT] Chat not found with ID in current bounds.", error);
				if (error.response && typeof error.response.data === 'string') {
					if (error.response.data.includes("not") && error.response.data.includes("permissions")) {
						chatNotAccessable();
					}
				}
				setLoadingChat(false);
				leaveChat();
				return;
			}
		}
		// If there is no chat ID, redirect to new chat.
		else {
			// Clear chat
			debugLog.log("[CHAT] No ID passed in, reverting to undefined.");
			setChat(undefined);
			document.title = `${env.REACT_APP_BRANDING_SYMPHONY_NAME ?? "Maestro"} - New Chat`;
		}
	};

	function chatNotAccessable() {
		createErrorToast("Sorry, You don't have access to that chat. Ask the chat owner to share it!");
	}

	// Exposed method to call when going to a certain chat ID.
	const selectChat = async (destChatId) => {
		debugLog.log("[CHAT] Entering chat with ID:", destChatId);
		navigate(`/${env.REACT_APP_BRANDING_APP_PATH ?? "maestro"}/chat/${destChatId}`, { replace: true });
		// NOTE: This triggers the above handleChatChange function.
		await setChatId(destChatId);
	};

	// Exposed method to call when leaving a chat ID. (deleting a chat, selecting New Chat button, error handling, etc.)
	const leaveChat = async (isShared = false) => {
		debugLog.log("[CHAT] Leaving chat. Entering menu view.");
		if (isShared) {
			try {
				await apiServiceClient.Chats.leaveSharedChat(chatId);
				debugLog.log("[CHAT] Successfully left shared chat.");
				dynamicDelete(chatId);
			} catch (error) {
				createErrorToast("An error occurred while leaving the shared chat. Please try again.");
				debugLog.error("Error leaving shared chat:", error);
			}
		}
		navigate(`/${env.REACT_APP_BRANDING_APP_PATH ?? "maestro"}/chat`, { replace: true });
		// NOTE: This triggers the above handleChatChange function.
		setChatId(undefined);
	};

	// Boolean flag to return if chatId is in a valid state or not.
	const isChatInContext = () => {
		return !!chatId;
	};

	// Exposed method that creates a chat if needed and returns an active chat ID.
	// opt is an optional parameter that will accept certain chat components such as assistant
	const fetchChatId = async (opt) => {
		debugLog.log("[CHAT] Checking if chatId is valid! CHATID:", chatId);
		if (!isChatInContext()) {
			if (chatIdRef.current) {
				debugLog.log("[CHAT] ChatId Ref found!", chatIdRef.current);
				return chatIdRef.current;
			}
			let newChatId = await createChat(opt);
			return newChatId;
		}
		return chatId;
	};

	// Creates a chat and returns the new ID.
	const createChat = async (opt) => {
		// Do we have an already existing or current chat? If not, let's create a new one.
		if (!chatId) {
			// create chat
			try {
				debugLog.log("[CHAT] Creating a new chat...");
				let resp = await apiServiceClient.Chats.createChat(opt);
				// This triggers the navigation as well.
				// await setChatId(resp.chatId);
				await selectChat(resp.id);
				//dynameNewChat();
				return resp.id;
			} catch (error) {
				debugLog.log("[CHAT] Failed to create a chat.");
				throw new Error(error);
			}
		}
	};

	// If the chat is pinned, unpins it. If the chat is unpinned, pins it.
	const togglePinChat = async () => {
		// The localPin is a fix for timing the isPinned update.
		// Since there is a delay, the latest isPinned is its inversion of the state going into the function call.
		let localPin = !isPinned;

		if (isPinned) {
			debugLog.log("[CHAT] Unpinning chat...");
			await apiServiceClient.Chats.unpinChat(chatId);
			setIsPinned(false);
		} else {
			debugLog.log("[CHAT] Pinning chat...");
			await apiServiceClient.Chats.pinChat(chatId);
			setIsPinned(true);
		}

		dynamicPin(chatId, localPin);
	};

	// Deletes the active chat.
	const deleteChat = async () => {
		// API call to use:
		debugLog.log("[CHAT] Deleting chat...");
		await apiServiceClient.Chats.deleteChat(chatId);
		dynamicDelete(chatId);

		// Reset chat ID (this navigates to /symphony)
		leaveChat();
	};

	// Renames the current chat.
	const renameChat = async (newName) => {
		// API call to use:
		debugLog.log("[CHAT] Renaming chat...");
		await apiServiceClient.Chats.renameChat(chatId, newName);
		dynamicRename(chatId, newName);
		// If we're NOT loading chat history, we need to update the document title.
		document.title = `${env.REACT_APP_BRANDING_SYMPHONY_NAME ?? "Maestro"} - ` + newName;
	};

	const exportChat = async () => {
		try {
			setIsExporting(true);
			// Make the POST request to the API
			const response = await apiServiceClient.Chats.ExportChat(chatId);
			if (!response || !response.isSuccess) {
				createErrorToast("An error occurred while converting the chat to a PDF. Please try again later.");
				throw new Error(response?.message);
			}
			// Create a temporary URL for the Blob
			const link = document.createElement("a");
			// link.href = `data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,${response.result}`            
			// link.download = `export_${new Date().toISOString()}.docx`; // Define the file name
			link.href = `data:application/pdf;base64,${response.result}`
			link.download = `export_${new Date().toISOString()}.pdf`; // Define the file name
			link.click();

		} catch (error) {
			debugLog.error("Error downloading PDF:", error);
			// alert('Failed to download the PDF. Please try again.');
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<ChatContext.Provider
			value={{
				// States
				chatId,
				chat,
				setChat,
				isPinned,
				setIsPinned,
				systemReservedTokens,
				chatIdRef,
				loadingChat,
				isExporting,
				// Functions
				processUrlParameters,
				selectChat,
				leaveChat,
				handleChatChange,
				isChatInContext,
				fetchChatId,
				togglePinChat,
				deleteChat,
				renameChat,
				exportChat,
			}}
		>
			{children}
		</ChatContext.Provider>
	);
}

// Hook to use the ChatContext in a component
function useChat() {
	const context = useContext(ChatContext);
	if (context === undefined) {
		throw new Error("useChat must be used within a ChatProvider");
	}
	return context;
}

export { ChatProvider, useChat };
